import { useState } from 'react';
import toast from 'react-hot-toast';

import { IconClose } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { useCreateFarm, useUpdateFarm } from '../contexts/farms';

import './styles/form.css';

const addressOf = (loc) => {
    if (!loc) return '';
    if (typeof loc === 'string') return loc;
    return loc.address || '';
};

// Alta/edición de una finca de un cliente (admin-farms.md). `name` + `owner_id`
// obligatorios; el dueño (cliente) viene fijado desde el detalle del cliente.
// `responsible` = encargado (nombre libre, usado en el informe reproductivo).
export default function FarmForm({ farm, client, onClose, onSaved }) {
    const editing = !!farm;
    const { mutateAsync: create, isPending: creating } = useCreateFarm();
    const { mutateAsync: update, isPending: updating } = useUpdateFarm(farm?.id);
    const busy = creating || updating;
    const cardRef = useModalDialog(onClose);

    const ownerName = client?.name || (farm ? `Cliente #${farm.owner_id}` : '—');

    const [name, setName] = useState(farm?.name || '');
    const [responsible, setResponsible] = useState(farm?.responsible || '');
    const [size, setSize] = useState(farm?.size != null ? String(farm.size) : '');
    const [location, setLocation] = useState(addressOf(farm?.location));
    const [remarks, setRemarks] = useState(typeof farm?.remarks === 'string' ? farm.remarks : '');

    const submit = async () => {
        if (!name.trim()) { toast.error('El nombre es obligatorio'); return; }
        if (size !== '' && !(Number(size) > 0)) { toast.error('El tamaño debe ser mayor a 0 (o vacío)'); return; }
        const payload = { name: name.trim() };
        if (!editing) payload.owner_id = client.id;
        payload.responsible = responsible.trim() || null;
        payload.size = size !== '' ? Number(size) : null;
        payload.location = location.trim() || null;
        payload.remarks = remarks.trim() || null;
        try {
            if (editing) await update(payload); else await create(payload);
            toast.success(editing ? 'Finca actualizada' : 'Finca creada');
            onSaved?.();
        } catch (er) {
            if (er?.status === 409) toast.error('Ya existe una finca con ese nombre para este cliente');
            else if (er?.status !== 403) toast.error(er?.message || 'No se pudo guardar la finca');
        }
    };

    return (
        <div className="farm-modal" role="dialog" aria-modal="true" aria-label={editing ? 'Editar finca' : 'Nueva finca'}>
            <div className="farm-modal__overlay" onClick={onClose} aria-hidden="true" />
            <div className="farm-modal__card" ref={cardRef}>
                <header className="farm-modal__head">
                    <div>
                        <h2 className="farm-modal__title">{editing ? 'Editar finca' : 'Nueva finca'}</h2>
                        <span className="farm-modal__sub">Cliente: {ownerName}</span>
                    </div>
                    <button type="button" className="farm-modal__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                <div className="farm-form">
                    <label className="farm-form__field">
                        <span className="farm-form__label">Nombre</span>
                        <input type="text" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} disabled={busy} />
                    </label>
                    <label className="farm-form__field">
                        <span className="farm-form__label">Encargado</span>
                        <input type="text" maxLength={160} value={responsible} onChange={(e) => setResponsible(e.target.value)} disabled={busy} placeholder="Opcional (responsable de la finca)" />
                    </label>
                    <div className="farm-form__row">
                        <label className="farm-form__field">
                            <span className="farm-form__label">Tamaño (ha)</span>
                            <input type="number" min="0" step="any" value={size} onChange={(e) => setSize(e.target.value)} disabled={busy} placeholder="Opcional" />
                        </label>
                        <label className="farm-form__field">
                            <span className="farm-form__label">Ubicación</span>
                            <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} disabled={busy} placeholder="Opcional (dirección/vereda)" />
                        </label>
                    </div>
                    <label className="farm-form__field">
                        <span className="farm-form__label">Notas</span>
                        <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={busy} placeholder="Opcional" />
                    </label>
                </div>

                <footer className="farm-modal__foot">
                    <button type="button" className="farm-btn farm-btn--ghost" onClick={onClose} disabled={busy}>Cancelar</button>
                    <button type="button" className="farm-btn farm-btn--primary" onClick={submit} disabled={busy || !name.trim()}>{busy ? 'Guardando…' : editing ? 'Guardar' : 'Crear finca'}</button>
                </footer>
            </div>
        </div>
    );
}
